import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import './style.css';

import Chessboard from '../chessboard';
import { getGameById } from '../../api/profile';
import { getFenAtIndex, getExplosionHistory } from '../../utils';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// chess.js's loadPgn rejects our custom 💣💥 explosion suffix and silently returns [].
// Instead we extract move tokens from the raw PGN text ourselves, preserving the suffix
// so getFenAtIndex can replay explosions correctly.
const parsePgn = (pgn) => {
    if (!pgn) return [];
    const moveText = pgn
        .replace(/\[[^\]]*\]/g, '')   // strip PGN headers [Key "Value"]
        .replace(/\{[^}]*\}/g, '')    // strip inline comments { ... }
        .replace(/\([^)]*\)/g, '');   // strip variation parentheses ( ... )
    return moveText
        .split(/\s+/)
        .filter(t => t && !/^\d+\.+$/.test(t) && !/^(1-0|0-1|1\/2-1\/2|\*)$/.test(t));
};

const playerName = (player) => player.is_guest ? 'Guest Player' : player.player_id;

const GameViewPage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    const [game, setGame] = useState(null);
    const [moves, setMoves] = useState([]);
    const [viewIndex, setViewIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // 'white' | 'black' — default from ?side= param, toggleable via button
    const [boardOrientation, setBoardOrientation] = useState(
        searchParams.get('side') === 'black' ? 'black' : 'white'
    );

    const moveListEndRef = useRef(null);

    useEffect(() => {
        getGameById(id)
            .then((data) => {
                setGame(data);
                const history = data.move_history?.length ? data.move_history : parsePgn(data.game_pgn);
                setMoves(history);
                setLoading(false);
            })
            .catch(() => {
                setError('Game not found.');
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        if (viewIndex >= moves.length) {
            moveListEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [moves, viewIndex]);

    if (loading) return <div className="game-view-loading">Loading game...</div>;
    if (error) return <div className="game-view-error">{error}</div>;

    const displayFen = getFenAtIndex(STARTING_FEN, moves, viewIndex);

    // Compute which bomb squares have detonated at or before the current viewIndex.
    const explosionHistory = getExplosionHistory(STARTING_FEN, moves);
    const detonatedSquares = new Set(
        explosionHistory.filter(e => e.moveCount < viewIndex).map(e => e.square)
    );

    // Show craters on squares where a bomb detonated; hide the bomb marker for those squares.
    const visibleCraters = [...detonatedSquares];
    const displayBombs = (game.bombs || [])
        .filter(sq => !detonatedSquares.has(sq))
        .map(sq => ({ square: sq, isOpponent: sq[1] === '5' || sq[1] === '6' }));

    const goToStart = () => setViewIndex(0);
    const goBack = () => setViewIndex(v => Math.max(0, v - 1));
    const goForward = () => setViewIndex(v => Math.min(moves.length, v + 1));
    const goToLatest = () => setViewIndex(moves.length);
    const flipBoard = () => setBoardOrientation(o => o === 'white' ? 'black' : 'white');

    const white = game.white_player;
    const black = game.black_player;

    const resultLabel = {
        WHITE_WINS: 'White wins',
        BLACK_WINS: 'Black wins',
        DRAW: 'Draw',
    }[game.result] ?? game.result;

    return (
        <div className="game-view-container">
            <div className="game-view-content">
                <div className="chess-wrapper">
                    <div className="player-info top">
                        <span className="player-name">
                            {playerName(black)}
                            {black.role === 'admin' && (
                                <span className="profile-tooltip-wrap">
                                    <span className="profile-role-badge">🌟</span>
                                    <span className="profile-tooltip">This user is an admin.</span>
                                </span>
                            )}
                            {black.role === 'mod' && (
                                <span className="profile-tooltip-wrap">
                                    <span className="profile-role-badge">⭐️</span>
                                    <span className="profile-tooltip">This user is a mod.</span>
                                </span>
                            )}
                        </span>
                        <span className="player-rating">
                            {black.elo_before_game_start}
                            {black.elo_change != null && (
                                <span style={{ color: black.elo_change >= 0 ? '#4ade80' : '#f87171', marginLeft: 4 }}>
                                    {black.elo_change >= 0 ? '+' : ''}{black.elo_change}
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="chess-board-container">
                        <Chessboard
                            displayFen={displayFen}
                            displayBombs={displayBombs}
                            visibleCraters={visibleCraters}
                            orientation={boardOrientation}
                        />
                    </div>
                    <div className="player-info bottom">
                        <span className="player-name">
                            {playerName(white)}
                            {white.role === 'admin' && (
                                <span className="profile-tooltip-wrap">
                                    <span className="profile-role-badge">🌟</span>
                                    <span className="profile-tooltip">This user is an admin.</span>
                                </span>
                            )}
                            {white.role === 'mod' && (
                                <span className="profile-tooltip-wrap">
                                    <span className="profile-role-badge">⭐️</span>
                                    <span className="profile-tooltip">This user is a mod.</span>
                                </span>
                            )}
                        </span>
                        <span className="player-rating">
                            {white.elo_before_game_start}
                            {white.elo_change != null && (
                                <span style={{ color: white.elo_change >= 0 ? '#4ade80' : '#f87171', marginLeft: 4 }}>
                                    {white.elo_change >= 0 ? '+' : ''}{white.elo_change}
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                <div className="side-panel">
                    <div className="game-result-banner">
                        <span className="result-text">{resultLabel}</span>
                        <span className="result-by">by {game.result_by.replace(/_/g, ' ').toLowerCase()}</span>
                    </div>

                    <div className="move-history-header">
                        <span className="moves-label">Moves</span>
                        <div className="nav-controls">
                            <button className="nav-btn" onClick={goToStart} title="Start">⏮</button>
                            <button className="nav-btn" onClick={goBack} title="Previous">◀</button>
                            <button className="nav-btn" onClick={goForward} title="Next">▶</button>
                            <button className="nav-btn" onClick={goToLatest} title="Latest">⏭</button>
                            <button className="nav-btn nav-btn--flip" onClick={flipBoard} title="Flip board">⇅</button>
                        </div>
                    </div>

                    <div className="move-history-table">
                        {Array.from({ length: Math.ceil(moves.length / 2) }).map((_, idx) => {
                            const whiteMoveIdx = 2 * idx + 1;
                            const blackMoveIdx = 2 * idx + 2;
                            return (
                                <div className="move-history-row" key={idx}>
                                    <div className="move-turn">{idx + 1}.</div>
                                    <div
                                        className={`move-white${viewIndex === whiteMoveIdx ? ' move-active' : ''}`}
                                        onClick={() => setViewIndex(whiteMoveIdx)}
                                    >
                                        {moves[2 * idx] || ''}
                                    </div>
                                    <div
                                        className={`move-black${viewIndex === blackMoveIdx ? ' move-active' : ''}`}
                                        onClick={() => moves[2 * idx + 1] && setViewIndex(blackMoveIdx)}
                                    >
                                        {moves[2 * idx + 1] || ''}
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={moveListEndRef} />
                    </div>

                    <div className="game-view-meta">
                        <span>{new Date(game.date).toLocaleDateString()}</span>
                        <span>{game.time_control}</span>
                    </div>

                    <Link to="/" className="game-view-back">← Back</Link>
                </div>
            </div>
        </div>
    );
};

export default GameViewPage;
